import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Heading,
  CheckBox,
  Select,
  FormField,
  TextArea,
  Tip,
  Layer,
} from "grommet";
import Markdown from "markdown-to-jsx";
import HotelService from "../../services/hotel.service";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { useFetchHotel } from "./redux/hooks";
import { PriceInput } from "@shared";
import { CURRENCY_OPTIONS_LIST, getFilteredPropsObject } from "@common/utils";
import { CircleInformation } from "grommet-icons";
import { CustomTabs } from "@features/shared/CustomTabs";
import { useTranslation } from "react-i18next";

const defaultValues = {
  deposit: 200,
  expressCheckout: true,
  autoSendEnabled: false,
  requestPhotoID: true,
  requestCardImprint: true,
  offerEarlyCheckin: true,
  offerUpgrade: true,
  offerLateCheckout: true,
  currency: "usd",
  onlineCheckinEnabled: false,
  checkinInvitationDisabled: false,
  // hotel options
  useDefaultCheckinText: true,
  checkinTextEn: "",
  checkinTextFr: "",
  registrationEnabled: false,
  registrationTextEn: "",
  registrationTextFr: "",
};

export default function SettingsCheckin() {
  const { t } = useTranslation();
  const { hotel } = useFetchHotel();
  const [formValues, setFormValues] = useState(defaultValues);
  const [showCustomCheckinHelpModal, setShowCustomCheckinHelpModal] =
    useState(false);
  const [showRegistrationPreview, setShowRegistrationPreview] = useState(false);
  const [activeRegistrationTab, setActiveRegistrationTab] = useState(0);

  useEffect(() => {
    setFormValues({ ...defaultValues, ...hotel });
  }, [hotel]);

  const handleCancel = () => {
    setFormValues({ ...defaultValues, ...hotel });
  };

  function handleInputChange({ target: { name, value } }) {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  }

  function handleCheckboxChange({ target: { name, checked } }) {
    setFormValues({ ...formValues, [name]: checked });
  }

  const handleOnSubmit = (e) => {
    e.preventDefault();

    // Validate registration text if registration is enabled
    if (formValues.registrationEnabled) {
      if (
        !formValues.registrationTextEn ||
        (hotel.language === "fr" && !formValues.registrationTextFr)
      ) {
        presentToastErrorContent({
          response: {
            data: {
              message: t("settingsCheckin.registrationTextRequired"),
            },
          },
        });
        return;
      }
    }

    const request = getFilteredPropsObject(formValues, defaultValues);
    HotelService.updateHotel(request).then(
      (response) => {
        if (response.data.ok) {
          presentToastSuccess(t("settingsCheckin.informationUpdated"));
        }
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const renderCheckbox = (label, name, disabled, tipContent) => (
    <Box direction="row" gap="small">
      <CheckBox
        name={name}
        checked={formValues[name]}
        onChange={handleCheckboxChange}
        label={label}
        disabled={disabled}
      />
      {tipContent && (
        <Tip content={tipContent}>
          <CircleInformation />
        </Tip>
      )}
    </Box>
  );

  const renderSelect = (label, name, options) => (
    <FormField label={label} width="medium">
      <Select
        size="medium"
        labelKey="label"
        valueKey={{ key: "value", reduce: true }}
        placeholder={t("common.select")}
        value={formValues[name]}
        options={options}
        onChange={({ value: nextValue }) =>
          setFormValues({ ...formValues, [name]: nextValue })
        }
      />
    </FormField>
  );

  const renderCheckinTextArea = (label) => {
    const name = "checkinText";
    const tabs = [
      {
        title: t("common.english"),
        key: `${name}En`,
        content: (
          <TextArea
            name={`${name}En`}
            value={formValues[`${name}En`] || ""}
            onChange={handleInputChange}
            style={{
              height: 200,
              ...(formValues.useDefaultCheckinText && { opacity: 0.5 }),
            }}
            disabled={formValues.useDefaultCheckinText}
            onClick={() =>
              setFormValues({ ...formValues, useDefaultCheckinText: false })
            }
          />
        ),
      },
      {
        title: "French",
        key: `${name}Fr`,
        content: (
          <TextArea
            name={`${name}Fr`}
            value={formValues[`${name}Fr`] || ""}
            onChange={handleInputChange}
            style={{
              height: 200,
              ...(formValues.useDefaultCheckinText && { opacity: 0.5 }),
            }}
            disabled={formValues.useDefaultCheckinText}
            onClick={() =>
              setFormValues({ ...formValues, useDefaultCheckinText: false })
            }
          />
        ),
      },
    ];

    const buttonStyle = (key) => {
      return (
        !formValues[key] && {
          color: "red",
        }
      );
    };

    return (
      <>
        {renderCheckbox(
          "Use default text",
          "useDefaultCheckinText",
          false,
          <>
            <p>
              <b>English</b>
              <br />
              Thank you for choosing The [hotelName]. We are looking forward to
              providing you with a memorable experience.
            </p>

            <p>
              <b>Français</b>
              <br />
              Merci d'avoir choisi [hotelName]. Nous avons hâte de vous offrir
              une expérience mémorable.
            </p>
          </>,
        )}
        <div style={{ position: "relative" }}>
          {formValues.useDefaultCheckinText && (
            <div
              style={{
                position: "absolute",
                top: 45,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
              }}
              onClick={() =>
                setFormValues({
                  ...formValues,
                  useDefaultCheckinText: false,
                })
              }
            />
          )}
          <CustomTabs tabs={tabs} buttonStyle={buttonStyle} />
        </div>
      </>
    );
  };

  const renderRegistrationTextArea = (label) => {
    const name = "registrationText";

    const tabs = [
      {
        title: "English",
        key: `${name}En`,
        content: (
          <TextArea
            name={`${name}En`}
            value={formValues[`${name}En`] || ""}
            onChange={handleInputChange}
            onFocus={() => setActiveRegistrationTab(0)}
            style={{ height: 600 }}
            disabled={!formValues.registrationEnabled}
          />
        ),
      },
      {
        title: "French",
        key: `${name}Fr`,
        content: (
          <TextArea
            name={`${name}Fr`}
            value={formValues[`${name}Fr`] || ""}
            onChange={handleInputChange}
            onFocus={() => setActiveRegistrationTab(1)}
            style={{ height: 600 }}
            disabled={!formValues.registrationEnabled}
          />
        ),
      },
    ];

    const buttonStyle = (key) => {
      return (
        !formValues[key] && {
          color: "red",
        }
      );
    };

    const handlePreviewClick = () => {
      setShowRegistrationPreview(true);
    };

    const getCurrentLanguage = () => {
      return activeRegistrationTab === 0 ? "en" : "fr";
    };

    const getCurrentText = () => {
      const lang = getCurrentLanguage();
      return formValues[`registrationText${lang === "en" ? "En" : "Fr"}`] || "";
    };

    return (
      <>
        {renderCheckbox("Registration Enabled", "registrationEnabled")}
        <Box gap="small" margin={{ top: "small" }}>
          <Button
            label={t("checkinFlow.preCheckin.preview")}
            size="small"
            onClick={handlePreviewClick}
            disabled={!formValues.registrationEnabled}
            alignSelf="start"
          />
          <CustomTabs
            tabs={tabs}
            buttonStyle={buttonStyle}
            onTabChange={(index) => setActiveRegistrationTab(index)}
            activeIndex={activeRegistrationTab}
          />
        </Box>
        {showRegistrationPreview && (
          <Layer
            onEsc={() => setShowRegistrationPreview(false)}
            onClickOutside={() => setShowRegistrationPreview(false)}
            responsive={false}
          >
            <Box pad="medium" gap="medium" width="large">
              <Heading level={3} margin="none">
                {t("settingsCheckin.registrationPreview", {
                  language:
                    getCurrentLanguage() === "en"
                      ? t("common.english")
                      : t("common.french"),
                })}
              </Heading>
              <Box
                background="white"
                pad="medium"
                round="small"
                border={{ color: "light-4" }}
                style={{ maxHeight: "80vh", overflow: "auto" }}
              >
                <Markdown>{getCurrentText()}</Markdown>
              </Box>
              <Button
                label={t("common.close")}
                onClick={() => setShowRegistrationPreview(false)}
                primary
              />
            </Box>
          </Layer>
        )}
      </>
    );
  };

  const handleShowCustomCheckinHelpModal = (e) => {
    e.preventDefault();
    setShowCustomCheckinHelpModal(true);
  };

  return (
    <div>
      <Heading level="1">{t("checkinFlow.preCheckin.toggle")}</Heading>

      <Box
        width={{ max: "large", width: "100%" }}
        gap="medium"
        background="light-2"
        round
        pad="medium"
      >
        <Heading level={3} margin="none">
          {t("settingsCheckin.defaultValues")}
        </Heading>
        {renderCheckbox(
          t("settingsCheckin.enableAutoSendKeys"),
          "autoSendEnabled",
        )}
        {renderCheckbox(t("settingsCheckin.enableOnlineCheckin"), "onlineCheckinEnabled")}
        {renderCheckbox(
          t("settingsCheckin.disableCheckinInvitation"),
          "checkinInvitationDisabled",
          !formValues.onlineCheckinEnabled,
        )}
        {renderCheckbox(
          t("settingsCheckin.requestPhotoId"),
          "requestPhotoID",
          !formValues.onlineCheckinEnabled,
        )}
        {renderCheckbox(
          t("settingsCheckin.requestCardImprint"),
          "requestCardImprint",
          !formValues.onlineCheckinEnabled,
        )}
        {renderCheckbox(t("settingsCheckin.enableExpressCheckout"), "expressCheckout")}
        {renderCheckbox(t("settingsRoomTypes.earlyCheckin"), "offerEarlyCheckin")}
        {renderCheckbox(t("settingsUpsell.offerRoomUpgrade"), "offerUpgrade")}
        {renderCheckbox(t("settingsUpsell.offerLateCheckout"), "offerLateCheckout")}
        <Box gap="medium" direction="row" wrap>
          <FormField label={t("checkinFlow.preCheckin.preAuthorizationDeposit")}>
            <PriceInput
              name="deposit"
              value={formValues.deposit}
              onChange={handleInputChange}
              disabled={!formValues.onlineCheckinEnabled}
            />
          </FormField>
          {renderSelect(t("checkinFlow.preCheckin.currency"), "currency", CURRENCY_OPTIONS_LIST)}
        </Box>
      </Box>
      <Box
        width={{ max: "large", width: "100%" }}
        gap="medium"
        background="light-2"
        round
        pad="medium"
        margin={{ top: "medium" }}
      >
        <Heading level={3} margin="none">
          {t("settingsCheckin.customCheckinText")}{" "}
          <a
            href="/#"
            onClick={handleShowCustomCheckinHelpModal}
            style={{ fontSize: "0.6em", paddingLeft: "10px" }}
          >
            {t("settingsCheckin.learnMore")}
          </a>
        </Heading>
        <ul>
          <li>
            {t("settingsCheckin.boldHelp")}
          </li>
          <li>
            {t("settingsCheckin.italicHelp")}
          </li>
          <li>
            {t("settingsCheckin.linkHelp")}
          </li>
        </ul>
        {renderCheckinTextArea(t("settingsCheckin.checkinInvitationText"))}
      </Box>
      <Box
        width={{ max: "large", width: "100%" }}
        gap="medium"
        background="light-2"
        round
        pad="medium"
        margin={{ top: "medium" }}
      >
        <Heading level={3} margin="none">
          {t("checkinFlow.preCheckin.registrationCardText")}
        </Heading>
        <Box gap="small">
          <p style={{ margin: "0 0 8px 0" }}>
            <strong>{t("checkinFlow.preCheckin.formattingGuide")}</strong>
          </p>
          <ul style={{ marginTop: 0 }}>
            <li>
              {t("settingsCheckin.boldGuide")}
            </li>
            <li>
              {t("settingsCheckin.italicGuide")}
            </li>
            <li>
              {t("settingsCheckin.linkGuide")}
            </li>
            <li>
              {t("settingsCheckin.heading1Guide")}
            </li>
            <li>
              {t("settingsCheckin.heading2Guide")}
            </li>
            <li>
              {t("settingsCheckin.numberedListGuide")}
            </li>
            <li>{t("settingsCheckin.lineBreakGuide")}</li>
          </ul>
        </Box>
        {renderRegistrationTextArea(t("settingsCheckin.registrationText"))}
      </Box>
      <Box direction="row" justify="between" margin={{ top: "medium" }}>
        <Button label={t("common.cancel")} onClick={handleCancel} />
        <Button type="submit" label={t("common.update")} primary onClick={handleOnSubmit} />
      </Box>
      {showCustomCheckinHelpModal && (
        <Layer
          onEsc={() => setShowCustomCheckinHelpModal(false)}
          onClickOutside={() => setShowCustomCheckinHelpModal(false)}
          responsive={false}
        >
          <Box pad="medium" gap="small" width={{ max: "large", width: "95vw" }}>
            <img
              src="/images/customChekinEmail.jpg"
              style={{ width: "100%", height: "100%" }}
              alt="Custom Check-in Email"
            />
            <Button
              label={t("common.close")}
              onClick={() => setShowCustomCheckinHelpModal(false)}
            />
          </Box>
        </Layer>
      )}
    </div>
  );
}
